import { createContext, useContext, useState, useEffect } from "react";
import {
  onIdTokenChanged,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  sendSignInLinkToEmail,
  fetchSignInMethodsForEmail,
  GoogleAuthProvider,
  signOut,
  updateProfile,
} from "firebase/auth";
import { auth } from "../firebase/config";

// localStorage key used to hand a pending Google credential off to JudgeRegister.js
// once the user completes the emailed link on this same browser (see loginWithGoogle
// below and the linking step in JudgeRegister.js).
export const PENDING_GOOGLE_LINK_KEY = "pendingGoogleLink";

// Shared by loginWithGoogle's account-linking fallback below, EventDashboard.js's
// admin-triggered invite, and JudgeRegister.js's self-serve "send me a new link" —
// keeps the sign-in-link URL construction in one place so the /judge/register
// continue-URL shape only needs to change in one file.
export const sendJudgeSignInLink = (email) => sendSignInLinkToEmail(auth, email, {
  url: `${window.location.origin}/judge/register?email=${encodeURIComponent(email)}`,
  handleCodeInApp: true,
});

// Fighter invites carry an eventId (judges never claim against a specific
// event; fighters do — see FighterRegister.js, which links the account to
// that event's fighter roster after sign-in) and land on the sibling
// /fighter/register route rather than /judge/register.
export const sendFighterSignInLink = (email, eventId) => sendSignInLinkToEmail(auth, email, {
  url: `${window.location.origin}/fighter/register?email=${encodeURIComponent(email)}&eventId=${encodeURIComponent(eventId)}`,
  handleCodeInApp: true,
});

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    // onIdTokenChanged (rather than onAuthStateChanged) also fires on token
    // refresh, not just sign-in/out - needed so the `admin` custom claim (set by
    // api/approveAdminRequest.js) shows up without a full re-login, via
    // refreshClaims below forcing a refresh right after approval.
    const unsub = onIdTokenChanged(auth, async (u) => {
      setUser(u);
      setIsAdmin(u ? (await u.getIdTokenResult()).claims.admin === true : false);
      setLoading(false);
    });
    return unsub;
  }, []);

  // Custom claims only ride along on a freshly-minted ID token. Call this right
  // after an admin request is approved so the UI picks up isAdmin immediately
  // instead of waiting for the token's normal ~1hr refresh cycle.
  const refreshClaims = async () => {
    if (!auth.currentUser) return;
    await auth.currentUser.getIdToken(true);
  };

  const loginWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    try {
      return await signInWithPopup(auth, provider);
    } catch (err) {
      if (err.code !== "auth/account-exists-with-different-credential") throw err;

      // Someone already has an account under this email via a different sign-in
      // method (almost always our email-link judge invite flow, see
      // EventDashboard.js's handleInviteOfficial). Google can't merge accounts
      // on its own, so: stash the Google credential we were just issued, email
      // them a fresh sign-in link, and complete the link (JudgeRegister.js)
      // once they click it on this same browser.
      const email = err.customData?.email;
      const pendingCredential = GoogleAuthProvider.credentialFromError(err);
      const methods = email ? await fetchSignInMethodsForEmail(auth, email) : [];
      if (!email || !pendingCredential || !methods.includes("emailLink")) throw err;

      window.localStorage.setItem(PENDING_GOOGLE_LINK_KEY, JSON.stringify({
        email, credential: pendingCredential.toJSON(),
      }));
      await sendJudgeSignInLink(email);
      const linkErr = new Error(
        `An account already exists for ${email} using an emailed sign-in link. We've sent a new link to that address — click it to finish linking your Google account.`
      );
      linkErr.code = "auth/link-email-sent";
      throw linkErr;
    }
  };

  const loginWithEmail = async (email, password) => {
    return signInWithEmailAndPassword(auth, email, password);
  };

  const registerWithEmail = async (email, password, displayName) => {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    if (displayName) {
      await updateProfile(result.user, { displayName });
    }
    await sendEmailVerification(result.user);
    return result;
  };

  const logout = async () => {
    return signOut(auth);
  };

  // updateProfile mutates auth.currentUser in place, so React won't see a change
  // unless we hand back a fresh object reference for the context's user state.
  const updateDisplayName = async (displayName) => {
    await updateProfile(auth.currentUser, { displayName });
    setUser({ ...auth.currentUser });
  };

  return (
    <AuthContext.Provider value={{ user, loading, isAdmin, refreshClaims, loginWithGoogle, loginWithEmail, registerWithEmail, logout, updateDisplayName }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
